import PreferredLanguageDropdown from "@/components/Register/preferredLanguageDropdown.component";
import { ValidationErrors } from "@/components/UserManagementComponents/Dialogs/DialogViewUser/viewUserDialog.component";
import { Checkbox } from "@/shadcn/components/ui/checkbox";
import { Input } from "@/shadcn/components/ui/input";
import { Label } from "@/shadcn/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shadcn/components/ui/select";
import { UserAdminUpdateProfileDetails } from "@/types";

type UserDetailsProps = {
  userDetails: UserAdminUpdateProfileDetails;
  setUserDetails: React.Dispatch<
    React.SetStateAction<UserAdminUpdateProfileDetails | null>
  >;
  errors: ValidationErrors;
};

const UserDetails: React.FC<UserDetailsProps> = ({
  userDetails,
  setUserDetails,
  errors,
}) => {
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      <div className="space-y-2">
        <Label htmlFor="fullName">
          Full Name <span className="text-red-500">*</span>
        </Label>
        <Input
          id="fullName"
          value={userDetails.fullName}
          onChange={(e) =>
            setUserDetails({
              ...userDetails,
              fullName: e.target.value,
            })
          }
          placeholder="Enter your full name"
        />
        {errors.fullName && (
          <div className="mt-1 text-xs text-red-500">{errors.fullName}</div>
        )}
      </div>
      <div className="space-y-2">
        <Label htmlFor="title">
          Title <span className="text-red-500">*</span>
        </Label>
        <Select
          value={userDetails.title || ""}
          onValueChange={(value: string) =>
            setUserDetails({ ...userDetails, title: value })
          }
        >
          <SelectTrigger className="w-full max-w-full truncate">
            <SelectValue placeholder="Select title" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Mr.">Mr.</SelectItem>
            <SelectItem value="Mrs.">Mrs.</SelectItem>
            <SelectItem value="Ms.">Ms.</SelectItem>
            <SelectItem value="Dr.">Dr.</SelectItem>
            <SelectItem value="Prof.">Prof.</SelectItem>
          </SelectContent>
        </Select>
        {errors.title && (
          <div className="mt-1 text-xs text-red-500">{errors.title}</div>
        )}
      </div>
      <div className="space-y-2">
        <Label htmlFor="designation">Designation</Label>
        <Input
          id="designation"
          value={userDetails.designation}
          onChange={(e) =>
            setUserDetails({
              ...userDetails,
              designation: e.target.value,
            })
          }
          placeholder="Enter your designation"
        />
        {errors.designation && (
          <div className="mt-1 text-xs text-red-500">{errors.designation}</div>
        )}
      </div>
      <PreferredLanguageDropdown
        selectedLanguage={userDetails.preferredLanguage.toLowerCase()}
        onLanguageChange={(language) =>
          setUserDetails({
            ...userDetails,
            preferredLanguage: language,
          })
        }
        error={errors.preferredLanguage || ""}
      />
      <div className="space-y-2">
        <Label htmlFor="email">
          Email <span className="text-red-500">*</span>
        </Label>
        <Input
          id="email"
          type="email"
          value={userDetails.email}
          onChange={(e) =>
            setUserDetails({
              ...userDetails,
              email: e.target.value.toLowerCase(),
            })
          }
          placeholder="Enter your email"
        />
        {errors.email && (
          <div className="mt-1 text-xs text-red-500">{errors.email}</div>
        )}
      </div>
      <div className="space-y-2">
        <Label htmlFor="phone">
          Phone <span className="text-red-500">*</span>
        </Label>
        <Input
          id="phone"
          type="tel"
          value={userDetails.phone_number}
          onChange={(e) =>
            setUserDetails({
              ...userDetails,
              phone_number: e.target.value,
            })
          }
          placeholder="Enter your phone number"
        />
        {errors.phone_number && (
          <div className="mt-1 text-xs text-red-500">{errors.phone_number}</div>
        )}
      </div>
      <div className="space-y-2">
        <Label htmlFor="preferredCommunicationMethod">
          Preferred Communication Method <span className="text-red-500">*</span>
        </Label>
        {/* Use checkboxes for multiselect because user can select multiple methods */}
        <div className="flex flex-col gap-2">
          <label className="flex items-center gap-2">
            <Checkbox
              id="preferredCommunicationMethodEmail"
              checked={userDetails.preferredCommunicationMethods.includes(
                "email"
              )}
              onCheckedChange={(e) => {
                setUserDetails({
                  ...userDetails,
                  preferredCommunicationMethods: e
                    ? [...userDetails.preferredCommunicationMethods, "email"]
                    : userDetails.preferredCommunicationMethods.filter(
                        (m) => m !== "email"
                      ),
                });
              }}
            />
            <span className="select-none">Email</span>
          </label>
          <label className="flex items-center gap-2">
            <Checkbox
              id="preferredCommunicationMethodsMS"
              checked={userDetails.preferredCommunicationMethods.includes(
                "sms"
              )}
              onCheckedChange={(e) => {
                setUserDetails({
                  ...userDetails,
                  preferredCommunicationMethods: e
                    ? [...userDetails.preferredCommunicationMethods, "sms"]
                    : userDetails.preferredCommunicationMethods.filter(
                        (m) => m !== "sms"
                      ),
                });
              }}
            />
            <span className="select-none">SMS</span>
          </label>
        </div>
        {errors.preferredCommunicationMethods && (
          <div className="mt-1 text-xs text-red-500">
            {errors.preferredCommunicationMethods}
          </div>
        )}
      </div>
    </div>
  );
};

export default UserDetails;
